import { Box, Checkbox, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { useIntl } from 'react-intl';

import { DatePickerProps, InfoIconTooltip, DateRangePicker } from '@verantos-dev/shared-components';

export type StudyPeriodRestrictionProps = {
  viewMode: boolean;
  isStudyPeriodRestrictionEnabled: boolean;
  setIsStudyPeriodRestrictionEnabled: (value: boolean) => void;
  studyPeriodStartDate: Dayjs | null;
  setStudyPeriodStartDate: (value: Dayjs | null) => void;
  studyPeriodEndDate: Dayjs | null;
  setStudyPeriodEndDate: (value: Dayjs | null) => void;
  setHasDateBoundsError: (value: boolean) => void;
};

export const StudyPeriodRestriction = ({
  viewMode,
  isStudyPeriodRestrictionEnabled,
  setIsStudyPeriodRestrictionEnabled,
  studyPeriodStartDate,
  setStudyPeriodStartDate,
  studyPeriodEndDate,
  setStudyPeriodEndDate,
  setHasDateBoundsError,
}: StudyPeriodRestrictionProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const disabled = !isStudyPeriodRestrictionEnabled;

  const onClickStudyPeriodRestrictionCheckbox = () => {
    setIsStudyPeriodRestrictionEnabled(!isStudyPeriodRestrictionEnabled);
  };

  const onStartDateChange = (_id: string, date: Dayjs | null) => {
    setStudyPeriodStartDate(date);
  };

  const onEndDateChange = (_id: string, date: Dayjs | null) => {
    setStudyPeriodEndDate(date);
  };

  const onError = (_id: string, detail: string | null) => {
    setHasDateBoundsError(detail !== null);
  };

  const baseConfigForDatePickers: Pick<
    DatePickerProps,
    'minDate' | 'maxDate' | 'disabled' | 'onError' | 'viewMode'
  > = {
    minDate: dayjs('1900-01-01'),
    maxDate: dayjs('2099-12-31'),
    disabled,
    onError,
    viewMode,
  };

  const startDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'study-period-start-date-picker',
    value: studyPeriodStartDate,
    onChange: onStartDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.studyPeriodDialogStudyPeriod.startDateLabel',
    }),
  };

  const endDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'study-period-end-date-picker',
    value: studyPeriodEndDate,
    onChange: onEndDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.studyPeriodDialogStudyPeriod.endDateLabel',
    }),
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary }} variant="body1">
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage..studyPeriodDialogStudyPeriod.label',
          })}
        </Typography>
        <InfoIconTooltip
          id="studyPeriod"
          width={383}
          title={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogStudyPeriod.info',
          })}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginY: theme.spacing(2),
        }}
      >
        <Checkbox
          sx={{
            marginLeft: `-${theme.spacing(3)}`,
          }}
          aria-checked={isStudyPeriodRestrictionEnabled}
          checked={isStudyPeriodRestrictionEnabled}
          onClick={onClickStudyPeriodRestrictionCheckbox}
          disabled={viewMode}
        />
        <Typography
          sx={{
            cursor: viewMode ? 'not-allowed' : 'pointer',
          }}
          variant="body1"
          onClick={onClickStudyPeriodRestrictionCheckbox}
        >
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogStudyPeriod.checkboxLabel',
          })}
        </Typography>
      </Box>

      <DateRangePicker
        id="study-period-date-picker"
        width={240}
        startDatePicker={startDatePicker}
        endDatePicker={endDatePicker}
        viewMode={viewMode}
      />
    </Box>
  );
};
